import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import s from './styles/StudentDocVaultTab.module.scss'
import list from 'pages/ApplicationDetailsPage/components/shared/ListRow/ListRow.module.scss'
import PdfPreviewPortal from 'pages/ApplicationDetailsPage/components/Checklist/components/PdfPreviewPortal/PdfPreviewPortal'
import DocumentChecklistRow from 'pages/ApplicationDetailsPage/components/shared/DocumentChecklistRow/DocumentChecklistRow'
import ListRow from 'pages/ApplicationDetailsPage/components/shared/ListRow/ListRow'
import NudgeStudentModal from 'widgets/NudgeStudentModal/NudgeStudentModal'
import AssignActivityModal from 'widgets/AssignActivityModal/AssignActivityModal'
import { applicationDocumentsStore } from 'pages/ApplicationDetailsPage/model/applicationDocumentsStore'
import {
	APPLICATION_DOCUMENT_GROUPS,
	APPLICATION_DOCUMENTS_MAX,
	countRequirements,
} from 'pages/ApplicationDetailsPage/constants/applicationDocumentCatalog'
import { buildAssignAllDocumentsPreset } from 'pages/ApplicationDetailsPage/utils/buildDocumentAssignItems'
import {
	downloadStudentApplicationDocument,
	uploadStudentApplicationDocument,
} from 'api/applicationDocuments'
import { validateUploadStudentDocumentFile } from 'widgets/UploadStudentDocumentModal/hooks/useUploadStudentDocumentModal'
import {
	getDocumentNudgeCooldownRemainingMs,
	isDocumentNudgeOnCooldown,
} from 'pages/ApplicationDetailsPage/utils/documentNudgeCooldown'
import { useSafeErrorBanner } from 'shared/components/ErrorBanner/useSafeErrorBanner'
import useStudentNewArtifacts from './hooks/useStudentNewArtifacts'
import { AppDetailIcon } from 'pages/ApplicationDetailsPage/icons/AppDetailIcon'
import { ReactComponent as DockIcon } from 'assets/icons/dock.svg'
import { ReactComponent as AnaliticIcon } from 'assets/icons/analitic.svg'
import { ReactComponent as FolderSharedIcon } from 'assets/icons/folder_shared.svg'

// Display copy for the three groups the vault has always had. A course refresh
// can introduce others (recommendations, essays, forms, language), which fall
// back to the group title the API sent and the default icon and placement.
const CATEGORY_META = {
	transcripts: { title: 'Transcripts', Icon: DockIcon },
	tests: { title: 'Test Scores', Icon: AnaliticIcon },
	portfolio: { title: 'Resume', Icon: FolderSharedIcon },
	recommendations: { title: 'Recommendations', Icon: FolderSharedIcon },
	essays: { title: 'Essays', Icon: DockIcon },
	forms: { title: 'Forms', Icon: DockIcon },
	language: { title: 'English Proficiency', Icon: AnaliticIcon },
}

const CATEGORY_PLACEMENT = {
	transcripts: s.cardTranscripts,
	tests: s.cardTests,
	portfolio: s.cardPortfolio,
}

const StudentDocVaultTab = observer(
	({ studentId = null, studentName = 'Student', isActive = false }) => {
		const { showErrorSafe } = useSafeErrorBanner()
		const newArtifacts = useStudentNewArtifacts(studentId, isActive)

		const [uploadingRequirementId, setUploadingRequirementId] = useState('')
		const [nudgeTarget, setNudgeTarget] = useState(null)
		const [nudgeCooldownTick, setNudgeCooldownTick] = useState(0)
		const [pdfPreview, setPdfPreview] = useState({
			open: false,
			url: '',
			title: '',
		})

		const [assignOpen, setAssignOpen] = useState(false)
		const [applicationModalInstanceKey, setApplicationModalInstanceKey] =
			useState(0)
		const [applicationAssignmentPreset, setApplicationAssignmentPreset] =
			useState(null)
		const [applicationAssignmentType, setApplicationAssignmentType] =
			useState('')

		useEffect(() => {
			if (studentId) applicationDocumentsStore.load(studentId)
		}, [studentId])

		const groups = applicationDocumentsStore.groups
		const docsLoading = applicationDocumentsStore.isLoading

		const groupsById = useMemo(
			() => Object.fromEntries(groups.map(group => [group.id, group])),
			[groups],
		)

		const summary = useMemo(() => {
			const docItems = groups.flatMap(group => group.items || [])
			const docUploaded = new Set(
				docItems
					.filter(item => item.counselorStatus === 'success' && item.requirementId)
					.map(item => item.requirementId),
			).size
			// Counted by requirement rather than by row, and taken from what the
			// API asked this student for: one requirement can render several file
			// rows, and the required set now varies with the courses they applied to.
			const total = countRequirements(groups) || APPLICATION_DOCUMENTS_MAX
			const uploaded = docUploaded
			const missing = Math.max(0, total - uploaded)
			const completion = total ? Math.round((uploaded / total) * 100) : 0
			return { total, uploaded, missing, completion }
		}, [groups])

		const newCount = newArtifacts.docCount

		useEffect(() => {
			const items = groups.flatMap(group => group.items || [])
			const cooling = items.filter(item =>
				isDocumentNudgeOnCooldown(item.lastNudgeAt),
			)
			if (!cooling.length) return undefined
			const remaining = Math.min(
				...cooling.map(item =>
					getDocumentNudgeCooldownRemainingMs(item.lastNudgeAt),
				),
			)
			if (remaining <= 0) {
				setNudgeCooldownTick(value => value + 1)
				return undefined
			}
			const timer = window.setTimeout(
				() => setNudgeCooldownTick(value => value + 1),
				remaining + 50,
			)
			return () => window.clearTimeout(timer)
		}, [groups, nudgeCooldownTick])

		const openAssignmentModal = useCallback(({ type, preset = null }) => {
			setApplicationAssignmentType(type)
			setApplicationAssignmentPreset(preset)
			setApplicationModalInstanceKey(value => value + 1)
			setAssignOpen(true)
		}, [])

		const handleAssign = useCallback(
			item => {
				openAssignmentModal({
					type: 'Document',
					preset: {
						locked: true,
						missingItems: [
							{
								id: item.id,
								requirementId: item.requirementId || item.id,
								title: item.title,
								action: 'upload_new',
							},
						],
						uploadedItems: [],
					},
				})
			},
			[openAssignmentModal],
		)

		const handleReupload = useCallback(
			item => {
				openAssignmentModal({
					type: 'Document',
					preset: {
						locked: true,
						missingItems: [],
						uploadedItems: [
							{
								id: item.id,
								requirementId: item.requirementId || item.id,
								title: item.title,
								action: 'reupload',
							},
						],
					},
				})
			},
			[openAssignmentModal],
		)

		const handleAssignAll = useCallback(() => {
			openAssignmentModal({
				type: 'Document',
				preset: buildAssignAllDocumentsPreset(
					applicationDocumentsStore.groups,
				),
			})
		}, [openAssignmentModal])

		const handleDirectUpload = useCallback(
			async (item, file) => {
				const rid = String(item?.requirementId || '').trim()
				if (!studentId || !rid) return
				const clientErr = validateUploadStudentDocumentFile(file)
				if (clientErr) {
					showErrorSafe(clientErr)
					return
				}
				setUploadingRequirementId(rid)
				try {
					await uploadStudentApplicationDocument(studentId, rid, file)
					await applicationDocumentsStore.reload()
				} catch (e) {
					showErrorSafe(
						e?.response?.data?.error ||
							e?.message ||
							'Upload failed. Please try again.',
					)
				} finally {
					setUploadingRequirementId('')
				}
			},
			[studentId, showErrorSafe],
		)

		const showPreview = useCallback((blob, title) => {
			const url = window.URL.createObjectURL(blob)
			setPdfPreview(prev => {
				if (prev?.url && String(prev.url).startsWith('blob:')) {
					try {
						window.URL.revokeObjectURL(prev.url)
					} catch {
						// noop
					}
				}
				return { open: true, url, title: title || 'Document' }
			})
		}, [])

		const handleViewDoc = useCallback(
			async item => {
				if (!studentId || !item?.fileId) return
				try {
					const blob = await downloadStudentApplicationDocument(
						studentId,
						item.fileId,
					)
					showPreview(blob, item.title)
				} catch {
					showErrorSafe('Failed to open document.')
				}
			},
			[studentId, showPreview, showErrorSafe],
		)

		const closePreview = useCallback(() => {
			setPdfPreview(prev => {
				if (prev?.url && String(prev.url).startsWith('blob:')) {
					try {
						window.URL.revokeObjectURL(prev.url)
					} catch {
						// noop
					}
				}
				return { open: false, url: '', title: '' }
			})
		}, [])

		// Render the groups the API actually returned, which include anything a
		// course refresh added. The hardcoded catalog is the fallback for a load
		// that has not landed yet or has failed, so the vault is never blank.
		const categoriesToRender = useMemo(() => {
			if (!groups.length) return APPLICATION_DOCUMENT_GROUPS
			return groups.map(group => ({
				id: group.id,
				title: group.title,
				requirements: (group.items || []).map(item => ({
					id: item.requirementId,
					title: item.title,
				})),
			}))
		}, [groups])

		const renderDocCategory = catGroup => {
			const meta = CATEGORY_META[catGroup.id]
			const Icon = meta?.Icon || DockIcon
			const group = groupsById[catGroup.id]
			const loading = docsLoading && !group
			const items =
				group?.items ||
				catGroup.requirements.map(req => ({
					requirementId: req.id,
					title: req.title,
					counselorStatus: 'missing',
				}))
			const uploaded = items.filter(
				item => item.counselorStatus === 'success',
			).length

			return (
				<section
					className={[s.card, CATEGORY_PLACEMENT[catGroup.id]]
						.filter(Boolean)
						.join(' ')}
					key={catGroup.id}
				>
					<header className={s.head}>
						<span className={s.head__icon}>
							<Icon />
						</span>
						<h3 className={s.head__title}>{meta?.title || catGroup.title}</h3>
						<span className={s.count}>
							{uploaded}/{items.length}
						</span>
					</header>
					<div className={s.rows}>
						{loading
							? items.map(item => (
									<ListRow
										key={item.requirementId}
										title=""
										isLoading
										className={list['row--docs']}
									/>
								))
							: items.map(item => (
									<DocumentChecklistRow
										key={`${item.requirementId}-${nudgeCooldownTick}`}
										item={item}
										onAssign={handleAssign}
										onUploadClick={handleDirectUpload}
										onNudge={row => setNudgeTarget(row)}
										onReupload={handleReupload}
										onView={handleViewDoc}
										nudgeOnCooldown={isDocumentNudgeOnCooldown(
											item.lastNudgeAt,
										)}
										isUploading={
											uploadingRequirementId === item.requirementId
										}
										isNew={
											item.fileId
												? newArtifacts.docFileIds.has(String(item.fileId))
												: false
										}
									/>
								))}
					</div>
				</section>
			)
		}

		return (
			<div className={s.wrap}>
				<div className={s.header}>
					<div className={s.header__info}>
						<h2 className={s.title}>Document Vault</h2>
						<p className={s.subtitle}>
							Securely store and manage all application artifacts
						</p>
					</div>
					<button
						type="button"
						className={s.assignAll}
						onClick={handleAssignAll}
					>
						<AppDetailIcon name="check" size={16} />
						Assign All
					</button>
				</div>

				<div className={s.kpis}>
					<div className={s.kpi}>
						<span className={s.kpi__label}>Total Documents</span>
						<span className={s.kpi__value}>{summary.total}</span>
					</div>
					<div className={s.kpi}>
						<span className={s.kpi__label}>New</span>
						<span className={[s.kpi__value, s.kpi__valueNew].join(' ')}>
							{newCount}
						</span>
					</div>
					<div className={s.kpi}>
						<span className={s.kpi__label}>Missing</span>
						<span className={[s.kpi__value, s.kpi__valueMissing].join(' ')}>
							{summary.missing}
						</span>
					</div>
					<div className={s.kpi}>
						<span className={s.kpi__label}>Completion %</span>
						<span className={[s.kpi__value, s.kpi__valueCompletion].join(' ')}>
							{summary.completion}%
						</span>
					</div>
				</div>

				<div className={s.grid}>
					{categoriesToRender.map(renderDocCategory)}
				</div>

				<AssignActivityModal
					isOpen={assignOpen}
					onClose={() => {
						setAssignOpen(false)
						setApplicationAssignmentPreset(null)
						setApplicationAssignmentType('')
					}}
					selectedStudents={[{ id: studentId, name: studentName }]}
					tepeModal="applications"
					initialAssignmentType={applicationAssignmentType}
					applicationDocumentPreset={applicationAssignmentPreset}
					applicationModalInstanceKey={applicationModalInstanceKey}
					missingItems={applicationDocumentsStore.missingItemsForAssign}
					uploadedItems={applicationDocumentsStore.uploadedItemsForAssign}
					clearSelectionOnOpen={false}
					lockRecipients
					onSuccess={async () => {
						await applicationDocumentsStore.reload()
					}}
				/>

				<NudgeStudentModal
					isOpen={Boolean(nudgeTarget)}
					onClose={() => setNudgeTarget(null)}
					studentId={studentId}
					studentName={studentName}
					documentTitle={nudgeTarget?.title || 'Document'}
					requirementId={nudgeTarget?.requirementId || ''}
					applicationDeadlineRaw=""
					onSuccess={() => {
						setNudgeTarget(null)
						applicationDocumentsStore.reload()
					}}
				/>

				<PdfPreviewPortal
					open={pdfPreview.open}
					url={pdfPreview.url}
					title={pdfPreview.title}
					onClose={closePreview}
				/>
			</div>
		)
	},
)

export default StudentDocVaultTab
